// algolia-conversion.js
/**
 * Algolia Conversion Tracking
 *
 * This file contains functions to track Algolia conversions through GTM.
 * It connects search data stored by the SearchInsights component with booking events.
 */

import aa from 'search-insights';
import logger from '../util/logger';

export const STORAGE_KEY = 'algolia_search_data';
export const DEFAULT_EXPIRATION_MS = 36 * 60 * 60 * 1000; // 36 hours in milliseconds
export const DEFAULT_CURRENCY = 'USD';
export const DEFAULT_INDEX_NAME = 'listings';

// Export conversion event names for consistency
export const CONVERSION_EVENTS = {
  BOOKING_REQUEST_SUBMITTED: 'Booking Request Submitted',
  NEW_INQUIRY_SUBMITTED: 'New Inquiry Submitted',
};

/**
 * Custom error class for Algolia tracker errors
 */
export class AlgoliaTrackerError extends Error {
  constructor(message) {
    super(message);
    this.name = 'AlgoliaTrackerError';
  }
}

/**
 * Creates an Algolia tracker with the given dependencies
 */
export function createTracker({
  aaClient,
  loggerInstance = logger,
  storage = typeof window !== 'undefined'
    ? window.localStorage
    : { getItem: () => null, removeItem: () => null },
  now = () => Date.now(),
  expirationMs = DEFAULT_EXPIRATION_MS,
  currency = DEFAULT_CURRENCY,
}) {
  if (!aaClient) {
    throw new AlgoliaTrackerError('aaClient is required');
  }

  if (!loggerInstance) {
    throw new AlgoliaTrackerError('loggerInstance is required');
  }

  let initialized = false;

  async function init({ appId, apiKey, useCookie = true }) {
    if (!appId) {
      throw new AlgoliaTrackerError('appId is required');
    }

    if (!apiKey) {
      throw new AlgoliaTrackerError('apiKey is required');
    }

    try {
      await aaClient('init', {
        appId,
        apiKey,
        useCookie,
      });
      initialized = true;
      return true;
    } catch (initError) {
      loggerInstance.error('Failed to initialize Algolia Insights:', initError);
      throw new AlgoliaTrackerError(`Failed to initialize Algolia: ${initError.message}`);
    }
  }

  function isInit() {
    return initialized;
  }

  // Set the authenticated user token globally
  function setAuthenticatedUser(userId) {
    if (!initialized) {
      throw new AlgoliaTrackerError('Algolia Insights not initialized');
    }

    if (!userId) {
      throw new AlgoliaTrackerError('userId is required');
    }

    try {
      aaClient('setAuthenticatedUserToken', userId);
      loggerInstance.info('Algolia authenticated user token set:', userId);
      return true;
    } catch (e) {
      loggerInstance.error('Error setting Algolia authenticated user token:', e);
      throw new AlgoliaTrackerError(`Failed to set authenticated user: ${e.message}`);
    }
  }

  // Track Algolia conversion for a booking event
  function trackConversion(eventName, value) {
    if (!initialized) {
      throw new AlgoliaTrackerError('Algolia Insights not initialized');
    }

    if (!eventName) {
      throw new AlgoliaTrackerError('eventName is required');
    }

    try {
      // Get stored search data
      const searchDataStr = storage.getItem(STORAGE_KEY);
      loggerInstance.debug('searchDataStr', searchDataStr);

      // If there is no search data, this is not an error - just means
      // this conversion is not related to search
      if (!searchDataStr) {
        loggerInstance.info('No search data found');
        return false;
      }

      let searchData;
      try {
        searchData = JSON.parse(searchDataStr);
      } catch (parseError) {
        loggerInstance.error('Error parsing search data:', parseError);
        return false;
      }

      // Don't count conversions after expiration time
      if (!searchData.timestamp || !searchData.queryID) {
        loggerInstance.info('Search data invalid - missing timestamp or queryID');
        return false;
      }

      if (now() - searchData.timestamp > expirationMs) {
        loggerInstance.info('Search data expired');
        return false;
      }

      // Log the conversion event for debugging
      loggerInstance.info('Tracking Algolia conversion:', {
        eventName,
        queryID: searchData.queryID,
        objectIDs: searchData.objectIDs,
        value,
      });

      // Send conversion event to Algolia
      aaClient('convertedObjectIDsAfterSearch', {
        eventName,
        index: searchData.indexName,
        queryID: searchData.queryID,
        objectIDs: searchData.objectIDs,
        currency,
        value: value || 0,
      });

      // Clear stored data to prevent duplicate conversion tracking
      storage.removeItem(STORAGE_KEY);
      return true;
    } catch (e) {
      loggerInstance.error('Error tracking Algolia conversion:', e);
      throw new AlgoliaTrackerError(`Failed to track conversion: ${e.message}`);
    }
  }

  // Track Algolia conversion directly without requiring search data
  function trackDirectConversion(eventName, value, objectID, indexName = DEFAULT_INDEX_NAME) {
    if (!initialized) {
      throw new AlgoliaTrackerError('Algolia Insights not initialized');
    }

    if (!eventName) {
      throw new AlgoliaTrackerError('eventName is required');
    }

    if (!objectID) {
      throw new AlgoliaTrackerError('objectID is required');
    }

    try {
      // Log the conversion event for debugging
      loggerInstance.info('Tracking Algolia direct conversion:', {
        eventName,
        objectIDs: [objectID],
        value,
      });

      // Send conversion event to Algolia
      aaClient('convertedObjectIDs', {
        eventName,
        index: indexName,
        objectIDs: [objectID],
        currency,
        value: value || 0,
      });
      return true;
    } catch (e) {
      loggerInstance.error('Error tracking Algolia direct conversion:', e);
      throw new AlgoliaTrackerError(`Failed to track direct conversion: ${e.message}`);
    }
  }

  return {
    init,
    isInit,
    setAuthenticatedUser,
    trackConversion,
    trackDirectConversion,
  };
}

// Skip auto-initialization in test environment
const isTestEnvironment = process.env.NODE_ENV === 'test';

// Create a default tracker instance for current usage
const defaultTracker = createTracker({
  aaClient: aa,
  loggerInstance: logger,
});

// Initialize if environment variables are available and not in test environment
if (!isTestEnvironment && typeof process !== 'undefined') {
  const algoliaAppId = process.env.REACT_APP_ALGOLIA_APP_ID;
  const algoliaApiKey = process.env.REACT_APP_ALGOLIA_SEARCH_KEY;

  if (algoliaAppId && algoliaApiKey) {
    defaultTracker
      .init({
        appId: algoliaAppId,
        apiKey: algoliaApiKey,
      })
      .catch(err => {
        logger.error('Failed to initialize default Algolia tracker:', err);
      });
  }
}

// Export compatibility functions that use the default tracker
export const setAlgoliaAuthenticatedUser = userId => defaultTracker.setAuthenticatedUser(userId);
export const trackAlgoliaConversion = (eventName, value) =>
  defaultTracker.trackConversion(eventName, value);
export const trackAlgoliaDirectConversion = (eventName, value, objectID, indexName) =>
  defaultTracker.trackDirectConversion(eventName, value, objectID, indexName);
